// EngineeringBooks.js
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  ScrollView,
  FlatList,
  Image,
  Dimensions,
  TextInput,
  Alert,
  Platform,
  Animated,
  Modal,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { 
  MaterialCommunityIcons, 
  Ionicons, 
  FontAwesome5, 
  Feather,
  AntDesign,
  Entypo
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import * as Sharing from 'expo-sharing';
import * as FileSystem from 'expo-file-system';
import { BlurView } from 'expo-blur';

const { width, height } = Dimensions.get('window');

// ─── JobEng Books API (live DB) ──────────────────────────────────────────────
const JE_API_BASE = 'https://api.jobeng.ai';
const JE_API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const jeHeaders = async () => {
  let tok = null;
  try {
    tok = (await AsyncStorage.getItem('auth_token')) ||
          (await AsyncStorage.getItem('userToken')) ||
          (await AsyncStorage.getItem('token'));
  } catch {}
  return { 'Content-Type': 'application/json', 'x-api-key': JE_API_KEY, ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
};

const jeNum = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
const jeStr = (v, d = '') => (v === null || v === undefined ? d : String(v));
const jeArr = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    const s = val.trim();
    if (!s) return [];
    if (s.startsWith('[')) { try { const p = JSON.parse(s); if (Array.isArray(p)) return p; } catch {} }
    return s.split(',').map((x) => x.trim()).filter(Boolean);
  }
  return [];
};

// Map a DB engineering_books row → this screen's book shape.
const jeMapBook = (r) => {
  const price = jeNum(r.price);
  const pdfUrl = jeStr(r.pdf_url || r.pdfUrl);
  const isFree = price === 0 || r.pdf_available === true || !!pdfUrl;
  return {
    id: jeStr(r.id || Math.random().toString(36).slice(2)),
    title: jeStr(r.title || r.name, 'Untitled Book'),
    author: jeStr(r.author, 'Unknown'),
    edition: jeStr(r.edition),
    publisher: jeStr(r.publisher),
    year: jeStr(r.year),
    isbn: jeStr(r.isbn),
    category: jeStr(r.category, 'general'),
    pages: jeNum(r.pages),
    level: jeStr(r.level, 'All Levels'),
    color: jeStr(r.color, '#0e4e96'),
    icon: jeStr(r.icon, 'book'),
    coverImage: jeStr(r.cover_image || r.coverImage || r.thumbnail),
    description: jeStr(r.description),
    topics: jeArr(r.topics),
    amazonLink: jeStr(r.amazon_link || r.amazonLink),
    pdfAvailable: r.pdf_available === true || !!pdfUrl,
    pdfUrl,
    rating: jeNum(r.rating, 4.5),
    price: isFree ? 'Free' : `£${price.toFixed(2)}`,
    priceValue: price,
    isFree,
    _raw: r,
  };
};

const EngineeringBooks = () => {
  const navigation = useNavigation();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [engineeringBooks, setEngineeringBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [bookmarkedBooks, setBookmarkedBooks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFormat, setSelectedFormat] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [sortBy, setSortBy] = useState('popularity');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedBook, setSelectedBook] = useState(null);
  const [showQuickView, setShowQuickView] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({});
  const [stats, setStats] = useState({
    totalBooks: 0,
    downloadedBooks: 0,
    readingProgress: 0,
    totalPages: 0,
  });
  
  const searchTimeoutRef = useRef(null);
  const scrollY = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Animation for header
  const headerHeight = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [180, 100],
    extrapolate: 'clamp',
  });

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 80],
    outputRange: [1, 0.8],
    extrapolate: 'clamp',
  });

  useFocusEffect(
    React.useCallback(() => {
      loadInitialData();
      startAnimations();
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, [])
  );

  useEffect(() => {
    filterAndSortBooks();
  }, [searchQuery, selectedCategory, selectedFormat, selectedLevel, sortBy, engineeringBooks]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load preferences
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
      
      // Load bookmarks
      const bookmarks = await AsyncStorage.getItem('bookmarkedBooks');
      if (bookmarks) {
        setBookmarkedBooks(JSON.parse(bookmarks));
      }
      
      // Load books data
      await loadEngineeringBooks();
      
      // Load stats
      await loadUserStats();
      
    } catch (error) {
      console.error('Error loading initial data:', error);
      Alert.alert('Error', 'Failed to load books data');
    } finally {
      setLoading(false);
    }
  };

  const loadEngineeringBooks = async () => {
    try {
      // One-time wipe of any stale book caches from earlier app versions.
      // Old caches stored books without a correct isFree flag, which made
      // free books render the Amazon button. Clear them so only fresh,
      // correctly-mapped DB data is ever shown.
      try {
        await AsyncStorage.multiRemove(['engineeringBooks', 'cache_engineeringBooks']);
      } catch {}

      // 1) Live DB first — shows ALL books (paid + free OpenStax PDFs)
      const h = await jeHeaders();
      const res = await fetch(`${JE_API_BASE}/api/data/engineering_books?limit=100`, { headers: h });
      if (res.ok) {
        const json = await res.json().catch(() => null);
        const rows = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
        const mapped = rows.map(jeMapBook);
        if (mapped.length) {
          setEngineeringBooks(mapped);
          setFilteredBooks(mapped);
          try { await AsyncStorage.setItem('cache_engineeringBooks_v2', JSON.stringify(mapped)); } catch {}
          return;
        }
      }
      // 2) Cached DB copy — re-map through jeMapBook in case it was stored
      //    in an older shape, so isFree is always recomputed correctly.
      const cached = await AsyncStorage.getItem('cache_engineeringBooks_v2');
      if (cached) {
        const books = JSON.parse(cached).map(jeMapBook);
        setEngineeringBooks(books);
        setFilteredBooks(books);
        return;
      }
      // 3) Mock fallback (offline / first run with no DB)
      const mockBooks = await getMockEngineeringBooks();
      setEngineeringBooks(mockBooks);
      setFilteredBooks(mockBooks);
    } catch (error) {
      console.error('Error loading engineering books:', error);
      const cached = await AsyncStorage.getItem('cache_engineeringBooks_v2');
      if (cached) {
        const books = JSON.parse(cached).map(jeMapBook);
        setEngineeringBooks(books);
        setFilteredBooks(books);
        return;
      }
      const mockBooks = await getMockEngineeringBooks();
      setEngineeringBooks(mockBooks);
      setFilteredBooks(mockBooks);
    }
  };

  const getMockEngineeringBooks = async () => {
    return [
      {
        id: 'B001',
        title: 'Mechanical Engineering Design',
        author: 'Joseph E. Shigley, Charles R. Mischke, Richard G. Budynas',
        edition: '11th Edition',
        publisher: 'McGraw-Hill',
        year: '2020',
        isbn: '978-0073398211',
        category: 'mechanical',
        pages: 1120,
        level: 'Undergraduate/Graduate',
        color: '#FF6B35',
        icon: 'cog',
        coverImage: 'https://images-na.ssl-images-amazon.com/images/I/81h7B+1hL4L.jpg',
        description: 'Comprehensive textbook covering machine design fundamentals with practical applications and examples. Perfect for mechanical engineering students and professionals.',
        topics: [
          'Stress Analysis',
          'Fatigue Failure',
          'Gears & Bearings',
          'Shaft Design',
          'Springs',
          'Bolted Connections',
          'Welded Joints'
        ],
        amazonLink: 'https://www.amazon.com/Mechanical-Engineering-Design-Joseph-Shigley/dp/0073398214',
        pdfAvailable: true,
        epubAvailable: true,
        mobiAvailable: true,
        rating: 4.7,
        reviews: 1284,
        price: '£75.99',
        discountPrice: '£68.99',
        featured: true,
        trending: true,
        chapters: 24,
        fileSize: '85 MB',
        language: 'English',
        publicationDate: '2020-03-15',
      },
      {
        id: 'B002',
        title: 'Introduction to Algorithms',
        author: 'Thomas H. Cormen, Charles E. Leiserson, Ronald L. Rivest, Clifford Stein',
        edition: '4th Edition',
        publisher: 'MIT Press',
        year: '2022',
        isbn: '978-0262046305',
        category: 'computer',
        pages: 1312,
        level: 'Graduate',
        color: '#3776AB',
        icon: 'chart-tree',
        coverImage: 'https://images-na.ssl-images-amazon.com/images/I/71eFQ-5dGML.jpg',
        description: 'The leading textbook on algorithms, used in universities worldwide. Comprehensive coverage of algorithm design, analysis, and implementation.',
        topics: [
          'Algorithm Analysis',
          'Data Structures',
          'Graph Algorithms',
          'Dynamic Programming',
          'NP-Completeness',
          'Parallel Algorithms'
        ],
        amazonLink: 'https://www.amazon.com/Introduction-Algorithms-fourth-Thomas-Cormen/dp/026204630X',
        pdfAvailable: true,
        epubAvailable: false,
        mobiAvailable: true,
        rating: 4.9,
        reviews: 2456,
        price: '£89.99',
        discountPrice: '£79.99',
        featured: true,
        trending: false,
        chapters: 35,
        fileSize: '120 MB',
        language: 'English',
        publicationDate: '2022-04-05',
      },
      {
        id: 'B003',
        title: 'Structural Analysis',
        author: 'R. C. Hibbeler',
        edition: '10th Edition',
        publisher: 'Pearson',
        year: '2017',
        isbn: '978-0134610672',
        category: 'civil',
        pages: 864,
        level: 'Undergraduate',
        color: '#0e4e96',
        icon: 'bridge',
        coverImage: 'https://images-na.ssl-images-amazon.com/images/I/81hQ7KcB8bL.jpg',
        description: 'Comprehensive coverage of structural analysis principles with emphasis on problem-solving skills and real-world applications.',
        topics: [
          'Structural Loads',
          'Truss Analysis',
          'Beam Deflection',
          'Moment Distribution',
          'Influence Lines',
          'Matrix Methods'
        ],
        amazonLink: 'https://www.amazon.com/Structural-Analysis-10th-R-Hibbeler/dp/0134610679',
        pdfAvailable: true,
        epubAvailable: true,
        mobiAvailable: false,
        rating: 4.6,
        reviews: 987,
        price: '£92.50',
        discountPrice: '£82.50',
        featured: false,
        trending: true,
        chapters: 18,
        fileSize: '72 MB',
        language: 'English',
        publicationDate: '2017-08-12',
      },
      {
        id: 'B004',
        title: 'Electric Machinery Fundamentals',
        author: 'Stephen J. Chapman',
        edition: '5th Edition',
        publisher: 'McGraw-Hill',
        year: '2011',
        isbn: '978-0073529547',
        category: 'electrical',
        pages: 704,
        level: 'Undergraduate',
        color: '#FFD700',
        icon: 'flash',
        coverImage: 'https://images-na.ssl-images-amazon.com/images/I/81n2D0i0+QL.jpg',
        description: 'Fundamental principles of electric machinery with MATLAB examples and practical applications for electrical engineering students.',
        topics: [
          'Transformers',
          'DC Machines',
          'AC Machines',
          'Synchronous Machines',
          'Induction Motors',
          'Special-Purpose Motors'
        ],
        amazonLink: 'https://www.amazon.com/Electric-Machinery-Fundamentals-Stephen-Chapman/dp/0073529540',
        pdfAvailable: true,
        epubAvailable: true,
        mobiAvailable: true,
        rating: 4.5,
        reviews: 654,
        price: '£68.99',
        discountPrice: '£61.99',
        featured: false,
        trending: false,
        chapters: 12,
        fileSize: '58 MB',
        language: 'English',
        publicationDate: '2011-02-01',
      },
      {
        id: 'B005',
        title: 'Chemical Engineering Design',
        author: 'Ray Sinnott, Gavin Towler',
        edition: '6th Edition',
        publisher: 'Butterworth-Heinemann',
        year: '2019',
        isbn: '978-0081025994',
        category: 'chemical',
        pages: 1200,
        level: 'Graduate',
        color: '#00CED1',
        icon: 'flask',
        coverImage: 'https://images-na.ssl-images-amazon.com/images/I/81x3VqZvS+L.jpg',
        description: 'Complete guide to chemical engineering design principles, process economics, safety, and environmental considerations.',
        topics: [
          'Process Design',
          'Equipment Selection',
          'Process Economics',
          'Safety Engineering',
          'Environmental Impact',
          'Plant Layout'
        ],
        amazonLink: 'https://www.amazon.com/Chemical-Engineering-Design-Sixth-Sinnott/dp/0081025999',
        pdfAvailable: true,
        epubAvailable: false,
        mobiAvailable: true,
        rating: 4.8,
        reviews: 432,
        price: '£94.99',
        discountPrice: '£85.49',
        featured: true,
        trending: false,
        chapters: 22,
        fileSize: '95 MB',
        language: 'English',
        publicationDate: '2019-10-15',
      },
      {
        id: 'B006',
        title: 'Engineering Mathematics',
        author: 'K. A. Stroud, Dexter J. Booth',
        edition: '8th Edition',
        publisher: 'Red Globe Press',
        year: '2020',
        isbn: '978-1352010275',
        category: 'mathematics',
        pages: 1264,
        level: 'Undergraduate',
        color: '#9B30FF',
        icon: 'calculator',
        coverImage: 'https://images-na.ssl-images-amazon.com/images/I/81Vk2C9oZAL.jpg',
        description: 'Comprehensive mathematics textbook specifically designed for engineering students with programmed learning approach.',
        topics: [
          'Calculus',
          'Linear Algebra',
          'Differential Equations',
          'Complex Numbers',
          'Fourier Analysis',
          'Probability & Statistics'
        ],
        amazonLink: 'https://www.amazon.com/Engineering-Mathematics-8th-K-Stroud/dp/1352010272',
        pdfAvailable: true,
        epubAvailable: true,
        mobiAvailable: true,
        rating: 4.7,
        reviews: 1789,
        price: '£45.99',
        discountPrice: '£41.39',
        featured: false,
        trending: true,
        chapters: 28,
        fileSize: '68 MB',
        language: 'English',
        publicationDate: '2020-07-30',
      },
      {
        id: 'B007',
        title: 'Fundamentals of Heat and Mass Transfer',
        author: 'Frank P. Incropera, David P. DeWitt, Theodore L. Bergman, Adrienne S. Lavine',
        edition: '8th Edition',
        publisher: 'Wiley',
        year: '2017',
        isbn: '978-1118989173',
        category: 'mechanical',
        pages: 1040,
        level: 'Undergraduate/Graduate',
        color: '#FF4500',
        icon: 'fire',
        coverImage: 'https://images-na.ssl-images-amazon.com/images/I/81s9O1jUVpL.jpg',
        description: 'Comprehensive treatment of heat and mass transfer phenomena with extensive examples and problems.',
        topics: [
          'Conduction',
          'Convection',
          'Radiation',
          'Heat Exchangers',
          'Mass Transfer',
          'Phase Change'
        ],
        amazonLink: 'https://www.amazon.com/Fundamentals-Heat-Mass-Transfer-Incropera/dp/1118989177',
        pdfAvailable: true,
        epubAvailable: true,
        mobiAvailable: false,
        rating: 4.6,
        reviews: 876,
        price: '£88.99',
        discountPrice: '£80.09',
        featured: false,
        trending: false,
        chapters: 16,
        fileSize: '78 MB',
        language: 'English',
        publicationDate: '2017-01-17',
      },
      {
        id: 'B008',
        title: 'Digital Design and Computer Architecture',
        author: 'David Harris, Sarah Harris',
        edition: '2nd Edition',
        publisher: 'Morgan Kaufmann',
        year: '2012',
        isbn: '978-0123944245',
        category: 'computer',
        pages: 712,
        level: 'Undergraduate',
        color: '#9370DB',
        icon: 'chip',
        coverImage: 'https://images-na.ssl-images-amazon.com/images/I/81yU-jJoW7L.jpg',
        description: 'Modern approach to digital design and computer architecture with HDL examples and system-level perspective.',
        topics: [
          'Digital Logic',
          'Combinational Circuits',
          'Sequential Circuits',
          'Computer Architecture',
          'Memory Systems',
          'I/O Systems'
        ],
        amazonLink: 'https://www.amazon.com/Digital-Design-Computer-Architecture-Harris/dp/0123944244',
        pdfAvailable: true,
        epubAvailable: true,
        mobiAvailable: true,
        rating: 4.7,
        reviews: 543,
        price: '£72.50',
        discountPrice: '£65.25',
        featured: true,
        trending: true,
        chapters: 14,
        fileSize: '62 MB',
        language: 'English',
        publicationDate: '2012-09-10',
      },
    ];
  };

  const loadUserStats = async () => {
    try {
      const statsData = await AsyncStorage.getItem('bookStats');
      if (statsData) {
        setStats(JSON.parse(statsData));
      } else {
        // Default stats
        const defaultStats = {
          totalBooks: engineeringBooks.length,
          downloadedBooks: 0,
          readingProgress: 0,
          totalPages: engineeringBooks.reduce((sum, book) => sum + (book.pages || 0), 0),
        };
        setStats(defaultStats);
        await AsyncStorage.setItem('bookStats', JSON.stringify(defaultStats));
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const startAnimations = () => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 800,
      useNativeDriver: true,
    }).start();
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadInitialData();
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const filterAndSortBooks = () => {
    let filtered = [...engineeringBooks];
    
    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(book =>
        String(book.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(book.author ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(book.category ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        book.topics?.some(topic => 
          topic.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(book => book.category === selectedCategory);
    }
    
    // Filter by format
    if (selectedFormat !== 'all') {
      filtered = filtered.filter(book => {
        if (selectedFormat === 'pdf') return book.pdfAvailable;
        if (selectedFormat === 'epub') return book.epubAvailable;
        if (selectedFormat === 'mobi') return book.mobiAvailable;
        return true;
      });
    }
    
    // Filter by level
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(book => {
        const bookLevel = String(book.level ?? '').toLowerCase();
        const selected = selectedLevel.toLowerCase();
        
        if (selected === 'beginner') {
          return bookLevel.includes('undergraduate') || bookLevel.includes('beginner');
        } else if (selected === 'intermediate') {
          return bookLevel.includes('intermediate') || bookLevel.includes('undergraduate');
        } else if (selected === 'advanced') {
          return bookLevel.includes('graduate') || bookLevel.includes('advanced');
        }
        return true;
      });
    }
    
    // Sort books
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'popularity':
          return (b.reviews || 0) - (a.reviews || 0);
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'newest':
          return new Date(b.publicationDate || b.year) - new Date(a.publicationDate || a.year);
        case 'price-low':
          const priceA = parseFloat(a.discountPrice || a.price || '0');
          const priceB = parseFloat(b.discountPrice || b.price || '0');
          return priceA - priceB;
        case 'price-high':
          const priceAHigh = parseFloat(a.discountPrice || a.price || '0');
          const priceBHigh = parseFloat(b.discountPrice || b.price || '0');
          return priceBHigh - priceAHigh;
        case 'pages':
          return (b.pages || 0) - (a.pages || 0);
        default:
          return 0;
      }
    });
    
    setFilteredBooks(filtered);
  };

  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      filterAndSortBooks();
    }, 300);
  };

  const toggleBookmark = async (bookId) => {
    try {
      let newBookmarks;
      if (bookmarkedBooks.includes(bookId)) {
        newBookmarks = bookmarkedBooks.filter(id => id !== bookId);
      } else {
        newBookmarks = [...bookmarkedBooks, bookId];
      }
      
      setBookmarkedBooks(newBookmarks);
      await AsyncStorage.setItem('bookmarkedBooks', JSON.stringify(newBookmarks));
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const downloadBook = async (book, format = 'pdf') => {
    try {
      setDownloadProgress(prev => ({
        ...prev,
        [book.id]: { downloading: true, progress: 0 }
      }));
      
      // Simulate download progress
      const interval = setInterval(() => {
        setDownloadProgress(prev => {
          const currentProgress = prev[book.id]?.progress || 0;
          if (currentProgress >= 100) {
            clearInterval(interval);
            return {
              ...prev,
              [book.id]: { downloading: false, progress: 100 }
            };
          }
          return {
            ...prev,
            [book.id]: { 
              downloading: true, 
              progress: Math.min(currentProgress + 10, 100) 
            }
          };
        });
      }, 200);
      
      // Simulate download completion
      setTimeout(async () => {
        clearInterval(interval);
        
        // Update stats
        const newStats = {
          ...stats,
          downloadedBooks: stats.downloadedBooks + 1,
        };
        setStats(newStats);
        await AsyncStorage.setItem('bookStats', JSON.stringify(newStats));
        
        // Save download history
        const downloadHistory = await AsyncStorage.getItem('downloadHistory') || '[]';
        const history = JSON.parse(downloadHistory);
        history.unshift({
          id: book.id,
          title: book.title,
          format: format,
          date: new Date().toISOString(),
          size: book.fileSize || 'Unknown',
        });
        await AsyncStorage.setItem('downloadHistory', JSON.stringify(history.slice(0, 50)));
        
        setDownloadProgress(prev => ({
          ...prev,
          [book.id]: { downloading: false, progress: 100 }
        }));
        
        Alert.alert(
          'Download Complete',
          `${book.title} has been downloaded successfully in ${format.toUpperCase()} format.`,
          [
            { text: 'Open', onPress: () => openDownloadedBook(book) },
            { text: 'OK', style: 'default' }
          ]
        );
        
      }, 2000);
      
    } catch (error) {
      console.error('Error downloading book:', error);
      setDownloadProgress(prev => ({
        ...prev,
        [book.id]: { downloading: false, progress: 0 }
      }));
      Alert.alert('Error', 'Failed to download book');
    }
  };

  const openDownloadedBook = (book) => {
    navigation.navigate('PDFViewer', { book });
  };

  const openQuickView = (book) => {
    setSelectedBook(book);
    setShowQuickView(true);
  };

  const shareBook = async (book) => {
    try {
      await Sharing.shareAsync(book.amazonLink, {
        mimeType: 'text/plain',
        dialogTitle: `Share: ${book.title}`,
      });
    } catch (error) {
      console.error('Error sharing book:', error);
    }
  };

  const categories = [
    { id: 'all', label: 'All Books', icon: 'book-multiple', count: engineeringBooks.length },
    { id: 'mechanical', label: 'Mechanical', icon: 'cog', color: '#FF6B35' },
    { id: 'civil', label: 'Civil', icon: 'bridge', color: '#0e4e96' },
    { id: 'electrical', label: 'Electrical', icon: 'flash', color: '#FFD700' },
    { id: 'computer', label: 'Computer', icon: 'laptop', color: '#3776AB' },
    { id: 'chemical', label: 'Chemical', icon: 'flask', color: '#00CED1' },
    { id: 'mathematics', label: 'Mathematics', icon: 'calculator', color: '#9B30FF' },
  ];

  const formats = [
    { id: 'all', label: 'All Formats', icon: 'format-list-bulleted' },
    { id: 'pdf', label: 'PDF', icon: 'file-pdf-box', color: '#ef4444' },
    { id: 'epub', label: 'EPUB', icon: 'book-open-variant', color: '#6366f1' },
    { id: 'mobi', label: 'MOBI', icon: 'kindle', color: '#f59e0b' },
  ];

  const levels = [
    { id: 'all', label: 'All Levels', icon: 'school' },
    { id: 'beginner', label: 'Beginner', icon: 'human-male-board', color: '#10b981' },
    { id: 'intermediate', label: 'Intermediate', icon: 'human-male-board-poll', color: '#f59e0b' },
    { id: 'advanced', label: 'Advanced', icon: 'human-capacity-increase', color: '#ef4444' },
  ];

  const sortOptions = [
    { id: 'popularity', label: 'Most Popular', icon: 'trending-up' },
    { id: 'rating', label: 'Highest Rated', icon: 'star' },
    { id: 'newest', label: 'Newest First', icon: 'calendar' },
    { id: 'price-low', label: 'Price: Low to High', icon: 'cash' },
    { id: 'price-high', label: 'Price: High to Low', icon: 'cash-multiple' },
    { id: 'pages', label: 'Most Pages', icon: 'book-open-page-variant' },
  ];

  const renderLoading = () => (
    <View style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <ActivityIndicator size={36} color="#6366f1" />
      <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#1f2937' }]}>
        Loading Engineering Books...
      </Text>
    </View>
  );

  const renderHeader = () => (
    <Animated.View 
      style={[
        styles.header,
        {
          height: headerHeight,
          opacity: headerOpacity,
        },
      ]}
    >
      <LinearGradient
        colors={darkMode ? ['#2d3748', '#4a5568'] : ['#6366f1', '#8b5cf6']}
        style={styles.headerGradient}
      >
        <View style={styles.headerTop}>
          <TouchableOpacity 
            style={styles.menuButton}
            onPress={() => navigation.toggleDrawer?.() || navigation.goBack()}
          >
            <MaterialCommunityIcons name="menu" size={24} color="#ffffff" />
          </TouchableOpacity>
          
          <Text style={styles.headerTitle}>📚 Engineering Books</Text>
          
          <View style={styles.headerActions}>
            <TouchableOpacity 
              style={styles.iconButton}
              onPress={() => navigation.navigate('Bookmarks')}
            >
              <MaterialCommunityIcons name="bookmark" size={24} color="#ffffff" />
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.iconButton}
              onPress={() => navigation.navigate('Downloads')}
            >
              <MaterialCommunityIcons name="download" size={24} color="#ffffff" />
            </TouchableOpacity>
          </View>
        </View>
        
        <Text style={styles.headerSubtitle}>
          Discover {engineeringBooks.length}+ engineering textbooks & references
        </Text>
        
        <View style={styles.statsContainer}>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>{stats.totalBooks}</Text>
            <Text style={styles.statLabel}>Books</Text>
          </View>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>{stats.downloadedBooks}</Text>
            <Text style={styles.statLabel}>Downloaded</Text>
          </View>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>{stats.totalPages.toLocaleString()}</Text>
            <Text style={styles.statLabel}>Total Pages</Text>
          </View>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>{stats.readingProgress}%</Text>
            <Text style={styles.statLabel}>Progress</Text>
          </View>
        </View>
      </LinearGradient>
    </Animated.View>
  );

  const renderSearchBar = () => (
    <View style={[styles.searchContainer, { 
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      shadowColor: darkMode ? '#000' : '#6366f1',
    }]}>
      <View style={styles.searchInputContainer}>
        <MaterialCommunityIcons 
          name="magnify" 
          size={20} 
          color={darkMode ? '#a0aec0' : '#6b7280'} 
        />
        <TextInput
          style={[styles.searchInput, { color: darkMode ? '#f7fafc' : '#1f2937' }]}
          placeholder="Search books, authors, topics..."
          placeholderTextColor={darkMode ? '#a0aec0' : '#6b7280'}
          value={searchQuery}
          onChangeText={handleSearch}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons 
              name="close-circle" 
              size={18} 
              color={darkMode ? '#a0aec0' : '#6b7280'} 
            />
          </TouchableOpacity>
        )}
      </View>
      
      <View style={styles.searchActions}>
        <TouchableOpacity
          style={[styles.searchActionButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
          onPress={() => setShowFilters(!showFilters)}
        >
          <MaterialCommunityIcons 
            name="filter-variant" 
            size={18} 
            color={darkMode ? '#ffffff' : '#1f2937'} 
          />
          <Text style={[styles.searchActionText, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            Filter
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.searchActionButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
          onPress={() => setShowSortOptions(!showSortOptions)}
        >
          <MaterialCommunityIcons 
            name="sort" 
            size={18} 
            color={darkMode ? '#ffffff' : '#1f2937'} 
          />
          <Text style={[styles.searchActionText, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            Sort
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.searchActionButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
          onPress={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
        >
          <MaterialCommunityIcons 
            name={viewMode === 'grid' ? 'view-list' : 'view-grid'} 
            size={18} 
            color={darkMode ? '#ffffff' : '#1f2937'} 
          />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderCategories = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false}
      style={styles.categoriesScroll}
      contentContainerStyle={styles.categoriesContainer}
    >
      {categories.map((category) => (
        <TouchableOpacity
          key={category.id}
          style={[
            styles.categoryButton,
            { backgroundColor: darkMode ? '#2d3748' : '#ffffff' },
            selectedCategory === category.id && { 
              backgroundColor: category.color || '#6366f1' 
            }
          ]}
          onPress={() => setSelectedCategory(category.id)}
        >
          <MaterialCommunityIcons 
            name={category.icon} 
            size={18} 
            color={selectedCategory === category.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[
            styles.categoryText,
            { color: selectedCategory === category.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
          ]}>
            {category.label}
          </Text>
          {category.count && (
            <View style={[
              styles.categoryCount,
              { backgroundColor: selectedCategory === category.id ? 'rgba(255,255,255,0.2)' : (darkMode ? '#4a5568' : '#e5e7eb') }
            ]}>
              <Text style={[
                styles.categoryCountText,
                { color: selectedCategory === category.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280') }
              ]}>
                {category.count}
              </Text>
            </View>
          )}
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowFilters(false)}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setShowFilters(false)}
      >
        <View style={[styles.filtersModal, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.filtersHeader}>
            <Text style={[styles.filtersTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              🔍 Filters
            </Text>
            <TouchableOpacity onPress={() => setShowFilters(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.filtersContent}>
            {/* Format Filter */}
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Format
              </Text>
              <View style={styles.filterOptions}>
                {formats.map((format) => (
                  <TouchableOpacity
                    key={format.id}
                    style={[
                      styles.filterOption,
                      { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' },
                      selectedFormat === format.id && { 
                        backgroundColor: format.color || '#6366f1' 
                      }
                    ]}
                    onPress={() => setSelectedFormat(format.id)}
                  >
                    <MaterialCommunityIcons 
                      name={format.icon} 
                      size={18} 
                      color={selectedFormat === format.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                    />
                    <Text style={[
                      styles.filterOptionText,
                      { color: selectedFormat === format.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
                    ]}>
                      {format.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            {/* Level Filter */}
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Difficulty Level
              </Text>
              <View style={styles.filterOptions}>
                {levels.map((level) => (
                  <TouchableOpacity
                    key={level.id}
                    style={[
                      styles.filterOption,
                      { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' },
                      selectedLevel === level.id && { 
                        backgroundColor: level.color || '#6366f1' 
                      }
                    ]}
                    onPress={() => setSelectedLevel(level.id)}
                  >
                    <MaterialCommunityIcons 
                      name={level.icon} 
                      size={18} 
                      color={selectedLevel === level.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                    />
                    <Text style={[
                      styles.filterOptionText,
                      { color: selectedLevel === level.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
                    ]}>
                      {level.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            {/* Action Buttons */}
            <View style={styles.filterActions}>
              <TouchableOpacity
                style={[styles.filterActionButton, styles.resetButton]}
                onPress={() => {
                  setSelectedCategory('all');
                  setSelectedFormat('all');
                  setSelectedLevel('all');
                }}
              >
                <Text style={styles.resetButtonText}>Reset All</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.filterActionButton, styles.applyButton]}
                onPress={() => setShowFilters(false)}
              >
                <Text style={styles.applyButtonText}>Apply Filters</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const renderSortOptionsModal = () => (
    <Modal
      visible={showSortOptions}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowSortOptions(false)}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setShowSortOptions(false)}
      >
        <View style={[styles.sortModal, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.sortHeader}>
            <Text style={[styles.sortTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              📊 Sort By
            </Text>
            <TouchableOpacity onPress={() => setShowSortOptions(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
          </View>
          
          <View style={styles.sortOptions}>
            {sortOptions.map((option) => (
              <TouchableOpacity
                key={option.id}
                style={[
                  styles.sortOption,
                  { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' },
                  sortBy === option.id && { backgroundColor: '#6366f1' }
                ]}
                onPress={() => {
                  setSortBy(option.id);
                  setShowSortOptions(false);
                }}
              >
                <MaterialCommunityIcons 
                  name={option.icon} 
                  size={20} 
                  color={sortBy === option.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                />
                <Text style={[
                  styles.sortOptionText,
                  { color: sortBy === option.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
                ]}>
                  {option.label}
                </Text>
                {sortBy === option.id && (
                  <MaterialCommunityIcons name="check" size={20} color="#ffffff" />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const renderBookGridItem = ({ item, index }) => (
    <Animated.View 
      style={[
        styles.bookGridItemInner,
        {
          opacity: fadeAnim,
          transform: [{
            translateY: fadeAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [50, 0],
            }),
          }],
        },
      ]}
    >
      <TouchableOpacity
        style={[styles.bookGridCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
        onPress={() => navigation.navigate('BookPreview', { book: item })}
        onLongPress={() => openQuickView(item)}
        activeOpacity={0.8}
      >
        <View style={styles.bookGridHeader}>
          <LinearGradient
            colors={[item.color, `${item.color}80`]}
            style={styles.bookGridIconContainer}
          >
            <MaterialCommunityIcons name={item.icon} size={24} color="#ffffff" />
          </LinearGradient>
          
          <TouchableOpacity 
            style={styles.bookmarkButton}
            onPress={() => toggleBookmark(item.id)}
          >
            <MaterialCommunityIcons
              name={bookmarkedBooks.includes(item.id) ? "bookmark" : "bookmark-outline"}
              size={20}
              color={bookmarkedBooks.includes(item.id) ? "#f59e0b" : (darkMode ? "#a0aec0" : "#6b7280")}
            />
          </TouchableOpacity>
        </View>
        
        <View style={styles.bookGridContent}>
          <Text style={[styles.bookGridTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]} numberOfLines={2}>
            {item.title}
          </Text>
          <Text style={[styles.bookGridAuthor, { color: darkMode ? '#a0aec0' : '#6b7280' }]} numberOfLines={1}>
            {item.author.split(',')[0]}
          </Text>
          
          <View style={styles.bookGridRating}>
            <View style={styles.ratingStars}>
              {[...Array(5)].map((_, i) => (
                <MaterialCommunityIcons
                  key={i}
                  name={i < Math.floor(item.rating) ? "star" : "star-outline"}
                  size={12}
                  color="#fbbf24"
                />
              ))}
            </View>
            <Text style={[styles.ratingText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {item.rating}
            </Text>
          </View>
          
          <View style={styles.bookGridMeta}>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="book-open-page-variant" size={12} color={darkMode ? '#a0aec0' : '#6b7280'} />
              <Text style={[styles.metaText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {item.pages}p
              </Text>
            </View>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="calendar" size={12} color={darkMode ? '#a0aec0' : '#6b7280'} />
              <Text style={[styles.metaText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                {item.year}
              </Text>
            </View>
          </View>
          
          <View style={styles.bookGridFormats}>
            {item.pdfAvailable && (
              <View style={[styles.formatBadge, { backgroundColor: '#fee2e2' }]}>
                <Text style={[styles.formatText, { color: '#dc2626' }]}>PDF</Text>
              </View>
            )}
            {item.epubAvailable && (
              <View style={[styles.formatBadge, { backgroundColor: '#dbeafe' }]}>
                <Text style={[styles.formatText, { color: '#1d4ed8' }]}>EPUB</Text>
              </View>
            )}
          </View>
          
          <View style={styles.priceContainer}>
            {item.discountPrice ? (
              <>
                <Text style={[styles.discountPrice, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  {item.discountPrice}
                </Text>
                <Text style={[styles.originalPrice, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {item.price}
                </Text>
              </>
            ) : (
              <Text style={[styles.price, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                {item.price}
              </Text>
            )}
          </View>
        </View>
        
        {item.featured && (
          <View style={styles.featuredBadge}>
            <Text style={styles.featuredText}>⭐ Featured</Text>
          </View>
        )}
        
        {downloadProgress[item.id]?.downloading && (
          <View style={styles.downloadOverlay}>
            <ActivityIndicator size={20} color="#ffffff" />
            <Text style={styles.downloadText}>
              {downloadProgress[item.id].progress}%
            </Text>
          </View>
        )}
      </TouchableOpacity>
      
      <View style={styles.bookGridActions}>
        <TouchableOpacity 
          style={styles.gridActionButton}
          onPress={() => openQuickView(item)}
        >
          <MaterialCommunityIcons name="eye" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.gridActionButton}
          onPress={() => {
            if (item.isFree && (item.pdfUrl || item.pdf_url)) {
              navigation.navigate('PDFViewer', { book: item });
            } else if (item.amazonLink) {
              Linking.openURL(item.amazonLink);
            } else {
              navigation.navigate('BookPreview', { book: item });
            }
          }}
        >
          <MaterialCommunityIcons 
            name={item.isFree ? 'book-open-variant' : 'cart-outline'} 
            size={16} 
            color={item.isFree ? '#16A34A' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.gridActionButton}
          onPress={() => shareBook(item)}
        >
          <MaterialCommunityIcons name="share-variant" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );

  const renderBookListItem = ({ item, index }) => (
    <Animated.View 
      style={[
        styles.bookListItem,
        {
          opacity: fadeAnim,
          transform: [{
            translateY: fadeAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [50, 0],
            }),
          }],
        },
      ]}
    >
      <TouchableOpacity
        style={[styles.bookListCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
        onPress={() => navigation.navigate('BookPreview', { book: item })}
        onLongPress={() => openQuickView(item)}
        activeOpacity={0.8}
      >
        <View style={styles.bookListLeft}>
          <LinearGradient
            colors={[item.color, `${item.color}80`]}
            style={styles.bookListIcon}
          >
            <MaterialCommunityIcons name={item.icon} size={28} color="#ffffff" />
          </LinearGradient>
        </View>
        
        <View style={styles.bookListContent}>
          <View style={styles.bookListHeader}>
            <View style={styles.bookListTitleContainer}>
              <Text style={[styles.bookListTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]} numberOfLines={1}>
                {item.title}
              </Text>
              <Text style={[styles.bookListAuthor, { color: darkMode ? '#a0aec0' : '#6b7280' }]} numberOfLines={1}>
                {item.author}
              </Text>
            </View>
            
            <TouchableOpacity 
              style={styles.bookmarkButton}
              onPress={() => toggleBookmark(item.id)}
            >
              <MaterialCommunityIcons
                name={bookmarkedBooks.includes(item.id) ? "bookmark" : "bookmark-outline"}
                size={20}
                color={bookmarkedBooks.includes(item.id) ? "#f59e0b" : (darkMode ? "#a0aec0" : "#6b7280")}
              />
            </TouchableOpacity>
          </View>
          
          <View style={styles.bookListMeta}>
            <View style={styles.metaRow}>
              <View style={styles.metaItem}>
                <MaterialCommunityIcons name="star" size={14} color="#fbbf24" />
                <Text style={[styles.metaText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                  {item.rating}/5 • {item.reviews} reviews
                </Text>
              </View>
              
              <View style={styles.metaItem}>
                <MaterialCommunityIcons name="book-open-page-variant" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
                <Text style={[styles.metaText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                  {item.pages} pages
                </Text>
              </View>
            </View>
            
            <View style={styles.metaRow}>
              <View style={styles.categoryBadge}>
                <Text style={[styles.categoryBadgeText, { color: '#ffffff', backgroundColor: item.color }]}>
                  {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                </Text>
              </View>
              
              <View style={styles.formatsContainer}>
                {item.pdfAvailable && (
                  <MaterialCommunityIcons name="file-pdf-box" size={16} color="#ef4444" />
                )}
                {item.epubAvailable && (
                  <MaterialCommunityIcons name="book-open-variant" size={16} color="#6366f1" />
                )}
                {item.mobiAvailable && (
                  <MaterialCommunityIcons name="tablet" size={16} color="#f59e0b" />
                )}
              </View>
            </View>
          </View>
          
          <Text style={[styles.bookListDescription, { color: darkMode ? '#cbd5e0' : '#4b5563' }]} numberOfLines={2}>
            {item.description}
          </Text>
          
          <View style={styles.bookListFooter}>
            <View style={styles.priceContainer}>
              {item.discountPrice ? (
                <>
                  <Text style={[styles.discountPrice, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {item.discountPrice}
                  </Text>
                  <Text style={[styles.originalPrice, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    {item.price}
                  </Text>
                </>
              ) : (
                <Text style={[styles.price, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  {item.price}
                </Text>
              )}
            </View>
            
            <View style={styles.bookListActions}>
              <TouchableOpacity 
                style={[styles.listActionButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
                onPress={() => openQuickView(item)}
              >
                <MaterialCommunityIcons name="eye" size={16} color={darkMode ? '#cbd5e0' : '#4b5563'} />
                <Text style={[styles.listActionText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                  Preview
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={[styles.listActionButton, { backgroundColor: item.isFree ? '#16A34A' : '#0e4e96' }]}
                onPress={() => {
                  if (item.isFree && (item.pdfUrl || item.pdf_url)) {
                    navigation.navigate('PDFViewer', { book: item });
                  } else if (item.amazonLink) {
                    Linking.openURL(item.amazonLink);
                  } else {
                    navigation.navigate('BookPreview', { book: item });
                  }
                }}
              >
                <MaterialCommunityIcons 
                  name={item.isFree ? 'book-open-variant' : 'cart-outline'} 
                  size={16} 
                  color="#ffffff" 
                />
                <Text style={[styles.listActionText, { color: '#ffffff' }]}>
                  {item.isFree ? 'Read' : 'Buy'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
        
        {item.featured && (
          <View style={styles.featuredBadge}>
            <Text style={styles.featuredText}>⭐ Featured</Text>
          </View>
        )}
      </TouchableOpacity>
    </Animated.View>
  );

  const renderQuickViewModal = () => (
    <Modal
      visible={showQuickView}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowQuickView(false)}
    >
      {selectedBook && (
        <View style={styles.quickViewModal}>
          <BlurView intensity={90} style={StyleSheet.absoluteFill} />
          
          <View style={[styles.quickViewContent, { backgroundColor: darkMode ? 'rgba(45, 55, 72, 0.95)' : 'rgba(255, 255, 255, 0.95)' }]}>
            <TouchableOpacity 
              style={styles.quickViewClose}
              onPress={() => setShowQuickView(false)}
            >
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
            
            <ScrollView style={styles.quickViewScroll} showsVerticalScrollIndicator={false}>
              <View style={styles.quickViewHeader}>
                <LinearGradient
                  colors={[selectedBook.color, `${selectedBook.color}80`]}
                  style={styles.quickViewIcon}
                >
                  <MaterialCommunityIcons name={selectedBook.icon} size={40} color="#ffffff" />
                </LinearGradient>
                
                <View style={styles.quickViewTitleContainer}>
                  <Text style={[styles.quickViewTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedBook.title}
                  </Text>
                  <Text style={[styles.quickViewAuthor, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    by {selectedBook.author}
                  </Text>
                </View>
              </View>
              
              <View style={styles.quickViewRating}>
                <View style={styles.quickViewStars}>
                  {[...Array(5)].map((_, i) => (
                    <MaterialCommunityIcons
                      key={i}
                      name={i < Math.floor(selectedBook.rating) ? "star" : "star-outline"}
                      size={20}
                      color="#fbbf24"
                    />
                  ))}
                </View>
                <Text style={[styles.quickViewRatingText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                  {selectedBook.rating}/5 • {selectedBook.reviews} reviews
                </Text>
              </View>
              
              <Text style={[styles.quickViewDescription, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                {selectedBook.description}
              </Text>
              
              <View style={styles.quickViewDetails}>
                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="book-open-page-variant" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <Text style={[styles.detailText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                    {selectedBook.pages} pages
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="calendar" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <Text style={[styles.detailText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                    Published {selectedBook.year}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="office-building" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <Text style={[styles.detailText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                    {selectedBook.publisher}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="school" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <Text style={[styles.detailText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                    {selectedBook.level}
                  </Text>
                </View>
              </View>
              
              <View style={styles.quickViewTopics}>
                <Text style={[styles.topicsTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Key Topics:
                </Text>
                <View style={styles.topicsList}>
                  {selectedBook.topics?.slice(0, 5).map((topic, index) => (
                    <View 
                      key={index} 
                      style={[styles.topicTag, { backgroundColor: darkMode ? '#4a5568' : '#e5e7eb' }]}
                    >
                      <Text style={[styles.topicText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        {topic}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
              
              <View style={styles.quickViewFormats}>
                <Text style={[styles.formatsTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Available Formats:
                </Text>
                <View style={styles.formatsList}>
                  {selectedBook.pdfAvailable && (
                    <TouchableOpacity 
                      style={[styles.formatButton, { backgroundColor: '#fee2e2' }]}
                      onPress={() => downloadBook(selectedBook, 'pdf')}
                    >
                      <MaterialCommunityIcons name="file-pdf-box" size={20} color="#dc2626" />
                      <Text style={[styles.formatButtonText, { color: '#dc2626' }]}>PDF</Text>
                    </TouchableOpacity>
                  )}
                  {selectedBook.epubAvailable && (
                    <TouchableOpacity 
                      style={[styles.formatButton, { backgroundColor: '#dbeafe' }]}
                      onPress={() => downloadBook(selectedBook, 'epub')}
                    >
                      <MaterialCommunityIcons name="book-open-variant" size={20} color="#1d4ed8" />
                      <Text style={[styles.formatButtonText, { color: '#1d4ed8' }]}>EPUB</Text>
                    </TouchableOpacity>
                  )}
                  {selectedBook.mobiAvailable && (
                    <TouchableOpacity 
                      style={[styles.formatButton, { backgroundColor: '#fef3c7' }]}
                      onPress={() => downloadBook(selectedBook, 'mobi')}
                    >
                      <MaterialCommunityIcons name="tablet" size={20} color="#d97706" />
                      <Text style={[styles.formatButtonText, { color: '#d97706' }]}>MOBI</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            </ScrollView>
            
            <View style={styles.quickViewActions}>
              <TouchableOpacity
                style={[styles.quickViewActionButton, styles.previewButton]}
                onPress={() => {
                  setShowQuickView(false);
                  navigation.navigate('BookPreview', { book: selectedBook });
                }}
              >
                <Text style={styles.previewButtonText}>View Full Details</Text>
              </TouchableOpacity>
              
              {selectedBook && (selectedBook.isFree && (selectedBook.pdfUrl || selectedBook.pdf_url)) ? (
                <TouchableOpacity
                  style={[styles.quickViewActionButton, { backgroundColor: '#16A34A' }]}
                  onPress={() => {
                    setShowQuickView(false);
                    navigation.navigate('PDFViewer', { book: selectedBook });
                  }}
                >
                  <MaterialCommunityIcons name="book-open-variant" size={16} color="#ffffff" />
                  <Text style={styles.amazonButtonText}>Read Book</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={[styles.quickViewActionButton, styles.amazonButton]}
                  onPress={() => {
                    setShowQuickView(false);
                    if (selectedBook?.amazonLink) Linking.openURL(selectedBook.amazonLink);
                  }}
                >
                  <FontAwesome5 name="amazon" size={16} color="#ffffff" />
                  <Text style={styles.amazonButtonText}>Buy on Amazon</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      )}
    </Modal>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name="book-search" size={64} color={darkMode ? '#4a5568' : '#d1d5db'} />
      <Text style={[styles.emptyStateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        No books found
      </Text>
      <Text style={[styles.emptyStateText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {searchQuery ? 'Try a different search term' : 'No books match your filters'}
      </Text>
      <TouchableOpacity
        style={styles.resetFiltersButton}
        onPress={() => {
          setSearchQuery('');
          setSelectedCategory('all');
          setSelectedFormat('all');
          setSelectedLevel('all');
        }}
      >
        <Text style={styles.resetFiltersText}>Reset Filters</Text>
      </TouchableOpacity>
    </View>
  );

  const renderFeaturedBooks = () => {
    const featured = engineeringBooks.filter(book => book.featured);
    if (featured.length === 0) return null;
    
    return (
      <View style={styles.featuredSection}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
            ⭐ Featured Books
          </Text>
          <TouchableOpacity onPress={() => navigation.navigate('FeaturedBooks')}>
            <Text style={styles.seeAllText}>See All</Text>
          </TouchableOpacity>
        </View>
        
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.featuredScroll}>
          {featured.map((book) => (
            <TouchableOpacity
              key={book.id}
              style={[styles.featuredCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
              onPress={() => navigation.navigate('BookPreview', { book })}
            >
              <LinearGradient
                colors={[book.color, `${book.color}80`]}
                style={styles.featuredGradient}
              >
                <MaterialCommunityIcons name={book.icon} size={32} color="#ffffff" />
              </LinearGradient>
              
              <View style={styles.featuredContent}>
                <Text style={[styles.featuredTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]} numberOfLines={2}>
                  {book.title}
                </Text>
                <Text style={[styles.featuredAuthor, { color: darkMode ? '#a0aec0' : '#6b7280' }]} numberOfLines={1}>
                  {book.author.split(',')[0]}
                </Text>
                
                <View style={styles.featuredRating}>
                  <View style={styles.ratingStars}>
                    {[...Array(5)].map((_, i) => (
                      <MaterialCommunityIcons
                        key={i}
                        name={i < Math.floor(book.rating) ? "star" : "star-outline"}
                        size={12}
                        color="#fbbf24"
                      />
                    ))}
                  </View>
                  <Text style={[styles.ratingText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    {book.rating}
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  const styles = StyleSheet.create({
    safeArea: {
      flex: 1,
      backgroundColor: '#6366f1',
    },
    container: {
      flex: 1,
      backgroundColor: '#f0f4f8',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    loadingText: {
      fontSize: 18,
      fontWeight: '600',
      marginTop: 16,
    },
    header: {
      overflow: 'hidden',
    },
    headerGradient: {
      paddingHorizontal: 20,
      paddingTop: Platform.OS === 'ios' ? 60 : 28,
      paddingBottom: 36,
      borderBottomLeftRadius: 24,
      borderBottomRightRadius: 24,
    },
    headerTop: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    menuButton: {
      padding: 8,
    },
    headerTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: '#ffffff',
      flex: 1,
      textAlign: 'center',
      marginHorizontal: 16,
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    iconButton: {
      padding: 8,
      marginLeft: 8,
    },
    headerSubtitle: {
      fontSize: 14,
      color: 'rgba(255, 255, 255, 0.9)',
      textAlign: 'center',
      marginBottom: 16,
    },
    statsContainer: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: 16,
      padding: 12,
    },
    statItem: {
      alignItems: 'center',
    },
    statNumber: {
      fontSize: 18,
      fontWeight: 'bold',
      color: '#ffffff',
      marginBottom: 4,
    },
    statLabel: {
      fontSize: 12,
      color: 'rgba(255, 255, 255, 0.8)',
    },
    searchContainer: {
      marginHorizontal: 20,
      marginTop: -28,
      marginBottom: 8,
      borderRadius: 16,
      padding: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 8,
    },
    searchInputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
      borderRadius: 12,
      paddingHorizontal: 16,
      paddingVertical: 12,
      marginBottom: 12,
    },
    searchInput: {
      flex: 1,
      fontSize: 16,
      marginHorizontal: 12,
    },
    searchActions: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    searchActionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 8,
      flex: 1,
      marginHorizontal: 4,
      justifyContent: 'center',
    },
    searchActionText: {
      fontSize: 14,
      marginLeft: 6,
    },
    categoriesScroll: {
      flexGrow: 0,
    },
    categoriesContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 4,
      marginBottom: 8,
    },
    categoryButton: {
      flexDirection: 'row',
      alignItems: 'center',
      height: 44,
      paddingHorizontal: 16,
      borderRadius: 22,
      marginRight: 8,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 6,
      elevation: 2,
    },
    categoryText: {
      fontSize: 14,
      fontWeight: '500',
      marginLeft: 6,
    },
    categoryCount: {
      marginLeft: 6,
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 10,
    },
    categoryCountText: {
      fontSize: 12,
      fontWeight: '600',
    },
    scrollView: {
      flex: 1,
      marginTop: 0,
    },
    contentContainer: {
      paddingBottom: 100,
    },
    featuredSection: {
      marginBottom: 24,
    },
    sectionHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 20,
      marginBottom: 12,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: 'bold',
    },
    seeAllText: {
      fontSize: 14,
      color: '#6366f1',
      fontWeight: '600',
    },
    featuredScroll: {
      paddingLeft: 20,
    },
    featuredCard: {
      width: 160,
      marginRight: 16,
      borderRadius: 16,
      padding: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    featuredGradient: {
      width: 64,
      height: 64,
      borderRadius: 32,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 12,
    },
    featuredContent: {
      flex: 1,
    },
    featuredTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      marginBottom: 4,
    },
    featuredAuthor: {
      fontSize: 12,
      marginBottom: 8,
    },
    featuredRating: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    ratingStars: {
      flexDirection: 'row',
      marginRight: 4,
    },
    ratingText: {
      fontSize: 12,
    },
    booksContainer: {
      paddingHorizontal: 20,
    },
    booksHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 16,
    },
    resultsCount: {
      fontSize: 14,
      color: darkMode ? '#a0aec0' : '#6b7280',
    },
    viewToggle: {
      flexDirection: 'row',
      backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
      borderRadius: 8,
      padding: 4,
    },
    viewToggleButton: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 6,
    },
    viewToggleButtonActive: {
      backgroundColor: '#6366f1',
    },
    viewToggleText: {
      fontSize: 14,
      color: darkMode ? '#a0aec0' : '#6b7280',
    },
    viewToggleTextActive: {
      color: '#ffffff',
    },
    gridContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    bookGridItem: {
      width: '48%',
      marginBottom: 16,
    },
    bookGridItemInner: {
      width: '100%',
    },
    bookGridCard: {
      borderRadius: 16,
      padding: 12,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      position: 'relative',
    },
    bookGridHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 12,
    },
    bookGridIconContainer: {
      width: 48,
      height: 48,
      borderRadius: 24,
      justifyContent: 'center',
      alignItems: 'center',
    },
    bookmarkButton: {
      padding: 4,
    },
    bookGridContent: {
      flex: 1,
    },
    bookGridTitle: {
      fontSize: 14,
      fontWeight: '700',
      marginBottom: 4,
      lineHeight: 19,
      minHeight: 38,
    },
    bookGridAuthor: {
      fontSize: 12,
      marginBottom: 8,
    },
    bookGridRating: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
    },
    bookGridMeta: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
    metaItem: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    metaText: {
      fontSize: 11,
      marginLeft: 4,
    },
    bookGridFormats: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginBottom: 8,
    },
    formatBadge: {
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 4,
      marginRight: 4,
      marginBottom: 4,
    },
    formatText: {
      fontSize: 10,
      fontWeight: '600',
    },
    priceContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    discountPrice: {
      fontSize: 16,
      fontWeight: 'bold',
      marginRight: 8,
    },
    originalPrice: {
      fontSize: 14,
      textDecorationLine: 'line-through',
    },
    price: {
      fontSize: 16,
      fontWeight: 'bold',
    },
    featuredBadge: {
      position: 'absolute',
      top: -6,
      right: -6,
      backgroundColor: '#fbbf24',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
    },
    featuredText: {
      fontSize: 10,
      color: '#ffffff',
      fontWeight: 'bold',
    },
    downloadOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      borderRadius: 16,
      justifyContent: 'center',
      alignItems: 'center',
    },
    downloadText: {
      color: '#ffffff',
      fontSize: 12,
      marginTop: 8,
    },
    bookGridActions: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      marginTop: 8,
    },
    gridActionButton: {
      padding: 8,
    },
    bookListItem: {
      marginBottom: 16,
    },
    bookListCard: {
      flexDirection: 'row',
      borderRadius: 16,
      padding: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      position: 'relative',
    },
    bookListLeft: {
      marginRight: 16,
    },
    bookListIcon: {
      width: 64,
      height: 64,
      borderRadius: 32,
      justifyContent: 'center',
      alignItems: 'center',
    },
    bookListContent: {
      flex: 1,
    },
    bookListHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 12,
    },
    bookListTitleContainer: {
      flex: 1,
      marginRight: 12,
    },
    bookListTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      marginBottom: 4,
    },
    bookListAuthor: {
      fontSize: 14,
    },
    bookListMeta: {
      marginBottom: 12,
    },
    metaRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    },
    categoryBadge: {
      borderRadius: 12,
      overflow: 'hidden',
    },
    categoryBadgeText: {
      fontSize: 12,
      fontWeight: '600',
      paddingHorizontal: 8,
      paddingVertical: 2,
    },
    formatsContainer: {
      flexDirection: 'row',
      gap: 8,
    },
    bookListDescription: {
      fontSize: 13,
      lineHeight: 18,
      marginBottom: 12,
    },
    bookListFooter: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    bookListActions: {
      flexDirection: 'row',
      gap: 8,
    },
    listActionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 8,
    },
    listActionText: {
      fontSize: 12,
      fontWeight: '600',
      marginLeft: 4,
    },
    emptyState: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 64,
      paddingHorizontal: 20,
    },
    emptyStateTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      marginTop: 16,
      marginBottom: 8,
    },
    emptyStateText: {
      fontSize: 16,
      textAlign: 'center',
      marginBottom: 24,
    },
    resetFiltersButton: {
      backgroundColor: '#6366f1',
      paddingHorizontal: 32,
      paddingVertical: 12,
      borderRadius: 8,
    },
    resetFiltersText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    filtersModal: {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      maxHeight: height * 0.8,
    },
    filtersHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 16,
      borderBottomWidth: 1,
      borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
    },
    filtersTitle: {
      fontSize: 20,
      fontWeight: 'bold',
    },
    filtersContent: {
      padding: 20,
    },
    filterSection: {
      marginBottom: 24,
    },
    filterSectionTitle: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: 12,
    },
    filterOptions: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    filterOption: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 20,
    },
    filterOptionText: {
      fontSize: 14,
      marginLeft: 6,
    },
    filterActions: {
      flexDirection: 'row',
      gap: 12,
      marginTop: 24,
    },
    filterActionButton: {
      flex: 1,
      paddingVertical: 14,
      borderRadius: 12,
      alignItems: 'center',
    },
    resetButton: {
      backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    },
    resetButtonText: {
      color: darkMode ? '#ffffff' : '#1f2937',
      fontSize: 16,
      fontWeight: '600',
    },
    applyButton: {
      backgroundColor: '#6366f1',
    },
    applyButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
    },
    sortModal: {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      padding: 20,
      maxHeight: height * 0.6,
    },
    sortHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
    },
    sortTitle: {
      fontSize: 20,
      fontWeight: 'bold',
    },
    sortOptions: {
      gap: 8,
    },
    sortOption: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 14,
      borderRadius: 12,
    },
    sortOptionText: {
      fontSize: 16,
      flex: 1,
      marginLeft: 12,
    },
    quickViewModal: {
      flex: 1,
      justifyContent: 'center',
      padding: 20,
    },
    quickViewContent: {
      borderRadius: 20,
      maxHeight: height * 0.8,
      overflow: 'hidden',
    },
    quickViewClose: {
      position: 'absolute',
      top: 16,
      right: 16,
      zIndex: 1,
      padding: 8,
    },
    quickViewScroll: {
      padding: 20,
    },
    quickViewHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 20,
    },
    quickViewIcon: {
      width: 64,
      height: 64,
      borderRadius: 32,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 16,
    },
    quickViewTitleContainer: {
      flex: 1,
    },
    quickViewTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      marginBottom: 4,
    },
    quickViewAuthor: {
      fontSize: 14,
    },
    quickViewRating: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 20,
    },
    quickViewStars: {
      flexDirection: 'row',
      marginRight: 8,
    },
    quickViewRatingText: {
      fontSize: 14,
    },
    quickViewDescription: {
      fontSize: 14,
      lineHeight: 20,
      marginBottom: 20,
    },
    quickViewDetails: {
      gap: 8,
      marginBottom: 20,
    },
    detailRow: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    detailText: {
      fontSize: 14,
      marginLeft: 8,
    },
    quickViewTopics: {
      marginBottom: 20,
    },
    topicsTitle: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: 12,
    },
    topicsList: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    topicTag: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
    },
    topicText: {
      fontSize: 12,
    },
    quickViewFormats: {
      marginBottom: 20,
    },
    formatsTitle: {
      fontSize: 16,
      fontWeight: '600',
      marginBottom: 12,
    },
    formatsList: {
      flexDirection: 'row',
      gap: 12,
    },
    formatButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 12,
    },
    formatButtonText: {
      fontSize: 14,
      fontWeight: '600',
      marginLeft: 8,
    },
    quickViewActions: {
      flexDirection: 'row',
      padding: 20,
      borderTopWidth: 1,
      borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
      gap: 12,
    },
    quickViewActionButton: {
      flex: 1,
      paddingVertical: 14,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    previewButton: {
      backgroundColor: '#6366f1',
    },
    previewButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
    },
    amazonButton: {
      backgroundColor: '#FF9900',
      flexDirection: 'row',
      alignItems: 'center',
    },
    amazonButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
      marginLeft: 8,
    },
  });

  if (loading) {
    return renderLoading();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar 
        barStyle="light-content" 
        backgroundColor="#6366f1" 
      />
      
      {renderHeader()}

      <Animated.ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#6366f1']}
            tintColor="#6366f1"
          />
        }
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: false }
        )}
      >
        {renderSearchBar()}
        {renderCategories()}
        {renderFeaturedBooks()}
        
        <View style={styles.booksContainer}>
          <View style={styles.booksHeader}>
            <Text style={styles.resultsCount}>
              {filteredBooks.length} books found
            </Text>
            
            <View style={styles.viewToggle}>
              <TouchableOpacity
                style={[styles.viewToggleButton, viewMode === 'grid' && styles.viewToggleButtonActive]}
                onPress={() => setViewMode('grid')}
              >
                <MaterialCommunityIcons 
                  name="view-grid" 
                  size={18} 
                  color={viewMode === 'grid' ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.viewToggleButton, viewMode === 'list' && styles.viewToggleButtonActive]}
                onPress={() => setViewMode('list')}
              >
                <MaterialCommunityIcons 
                  name="view-list" 
                  size={18} 
                  color={viewMode === 'list' ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                />
              </TouchableOpacity>
            </View>
          </View>
          
          {filteredBooks.length === 0 ? (
            renderEmptyState()
          ) : viewMode === 'grid' ? (
            <View style={styles.gridContainer}>
              {filteredBooks.map((book, index) => (
                <View key={book.id} style={styles.bookGridItem}>
                  {renderBookGridItem({ item: book, index })}
                </View>
              ))}
            </View>
          ) : (
            <FlatList
              data={filteredBooks}
              keyExtractor={(item) => item.id}
              renderItem={renderBookListItem}
              scrollEnabled={false}
              showsVerticalScrollIndicator={false}
            />
          )}
        </View>
        
        {/* Spacer for bottom padding */}
        <View style={{ height: 100 }} />
      </Animated.ScrollView>
      
      {/* Floating Action Button */}
      <TouchableOpacity
        style={[styles.fab, { backgroundColor: '#6366f1' }]}
        onPress={() => navigation.navigate('BookScanner')}
      >
        <MaterialCommunityIcons name="barcode-scan" size={24} color="#ffffff" />
      </TouchableOpacity>
      
      {/* Modals */}
      {renderFiltersModal()}
      {renderSortOptionsModal()}
      {renderQuickViewModal()}
    </SafeAreaView>
  );
};

export default EngineeringBooks;